import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowUpRight, Quote } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import { api } from "../lib/api";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // api is a stable module-level axios instance; setReviews/setError/setLoading are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/testimonials");
        setReviews(data);
      } catch (e) {
        setError("Could not load reviews. Please try again later.");
      }
      setLoading(false);
    })();
  }, []);

  const stats = useMemo(() => {
    if (!reviews.length) return null;
    const total = reviews.length;
    const avg = reviews.reduce((s, r) => s + r.stars, 0) / total;
    const fives = reviews.filter((r) => r.stars === 5).length;
    return { total, avg: Math.round(avg * 10) / 10, fives };
  }, [reviews]);

  // stats is derived from reviews via useMemo; reviews is the true dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!reviews.length) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Xion Labs",
      url: "https://xion-digital-studio.preview.emergentagent.com",
      founder: { "@type": "Person", name: "Vedant Baligar" },
      description:
        "Design & engineering studio for custom presentations, software, 3D models, animated websites, and cinematic videos.",
      aggregateRating: stats && {
        "@type": "AggregateRating",
        ratingValue: stats.avg.toFixed(1),
        reviewCount: stats.total,
        bestRating: 5,
        worstRating: 1,
      },
      review: reviews.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.customer_name || "Anonymous" },
        reviewBody: r.comment,
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.stars,
          bestRating: 5,
          worstRating: 1,
        },
        itemReviewed: {
          "@type": "Service",
          name: r.service_type || "Xion Labs service",
        },
        datePublished: r.created_at,
      })),
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(ld);
    tag.id = "xion-reviews-ld";
    document.querySelector("#xion-reviews-ld")?.remove();
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [reviews, stats]);

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />
      <section className="pt-36 pb-12 relative overflow-hidden">
        <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] bg-cyan-500/15 blur-[140px] rounded-full" />
        <div className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] bg-violet-500/15 blur-[140px] rounded-full" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ verified reviews</p>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tight max-w-3xl">
              What clients say<br />about <span className="text-gradient-cyan">Xion</span>.
            </h1>
            {stats && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="glass-card rounded-2xl px-6 py-5 min-w-[280px]"
              >
                <div className="flex items-center gap-3">
                  <div className="font-display font-black text-5xl text-gradient-cyan">{stats.avg}</div>
                  <div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={`star-avg-${i}`}
                          className={`w-4 h-4 ${i < Math.round(stats.avg) ? "fill-yellow-400 text-yellow-400" : "text-slate-700"}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72] mt-1.5">
                      {stats.total} review{stats.total === 1 ? "" : "s"} · {stats.fives} five-star
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
          <p className="text-[#5A5A72] mt-6 max-w-2xl">
            Every review here was submitted directly by a real Xion client through our project tracking emails — no purchases, no incentives, no edits.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {loading ? (
            <Loader label="LOADING REVIEWS..." />
          ) : error ? (
            <div className="glass-card rounded-2xl p-12 text-center" data-testid="reviews-error">
              <p className="text-red-400">{error}</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center" data-testid="reviews-empty">
              <Star className="w-10 h-10 mx-auto text-[#5A5A72] mb-3" />
              <p className="font-display text-2xl">First reviews coming soon.</p>
              <p className="text-[#6B7280] mt-2 max-w-md mx-auto">
                Approved 5-star ratings from real Xion projects will appear here as our clients ship.
              </p>
              <Link to="/quote" className="mt-6 inline-flex btn-primary px-5 py-3 rounded-lg text-sm font-semibold">
                Be the first <ArrowUpRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((r, i) => (
                <motion.article
                  key={r.id}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.6 }}
                  whileHover={{ y: -4 }}
                  data-testid={`review-${r.id}`}
                  className="glass-card neon-border rounded-2xl p-7 transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, n) => (
                        <Star key={`star-${n}`} className={`w-4 h-4 ${n < r.stars ? "fill-yellow-400 text-yellow-400" : "text-slate-700"}`} />
                      ))}
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#00D4FF] bg-[#00D4FF]/5 border border-[#00D4FF]/20 px-2 py-1 rounded-full">Verified</span>
                  </div>
                  <Quote className="w-5 h-5 text-[#00D4FF]/60 mb-2" />
                  <p className="text-[#374151] leading-relaxed">{r.comment}</p>
                  <div className="mt-6 pt-5 border-t border-black/[0.06]">
                    <p className="font-display font-bold">{r.customer_name}</p>
                    {(r.service_type || r.project_title) && (
                      <p className="text-xs text-[#6B7280] font-mono uppercase tracking-wider mt-1">
                        {[r.service_type, r.project_title].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="text-[10px] font-mono text-[#5A5A72] mt-1.5">
                      {new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short" })}
                    </p>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight">
            Add your story to the wall.
          </h2>
          <p className="text-[#5A5A72] mt-4">
            Hire Xion. Ship something great. Click a star in the email — your review lives here.
          </p>
          <Link to="/quote" data-testid="reviews-cta-quote" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-primary text-sm font-semibold">
            Start a project <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
